// components/marketer/Dashboard.jsx   (100% COMPLETE FINAL VERSION)
import { useState, useEffect } from "react";
import { 
  Users, Building2, DollarSign, TrendingUp, Menu, X, 
  UserPlus, Building, FileText, LogOut, Calendar, Mail, Phone, Loader2
} from "lucide-react";
import { Activity } from "lucide-react";
export default function MarketerDashboard() {
  const [tab, setTab] = useState("dashboard");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [data, setData] = useState({ 
    stats: {}, 
    seekers: [], 
    employers: [] 
  });
 
  const [loading, setLoading] = useState(true);
  const [selectedSeeker, setSelectedSeeker] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}marketer/dashboard`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
      });
      const d = await res.json();
      if (d.success) setData(d);
    } catch (err) {
      alert("Error loading dashboard");
    }
    setLoading(false);
  };

  const navItems = [
    { id: "dashboard", name: "Dashboard", icon: <TrendingUp className="w-5 h-5" /> },
    { id: "create-seeker", name: "Create Seeker", icon: <UserPlus className="w-5 h-5" /> },
    { id: "create-employer", name: "Create Employer", icon: <Building className="w-5 h-5" /> },
    { id: "my-seekers", name: "My Seekers", icon: <Users className="w-5 h-5" /> },
    { id: "my-employers", name: "My Employers", icon: <Building2 className="w-5 h-5" /> },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-16 h-16 animate-spin text-purple-600 mx-auto mb-4" />
          <p className="text-xl">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile Menu Button */}
      <button 
        onClick={() => setSidebarOpen(!sidebarOpen)} 
        className="lg:hidden fixed top-4 left-4 z-50 p-3 bg-purple-600 text-white rounded-lg shadow-lg"
      >
        {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
      </button>

      {/* Sidebar */}
      <div className={`fixed inset-y-0 left-0 z-40 w-64 bg-white shadow-2xl transform transition-transform lg:translate-x-0 ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}`}>
        <div className="p-6 border-b bg-purple-600 text-white">
          <h2 className="text-2xl font-bold">Marketer Portal</h2>
          <p className="text-sm opacity-90">Earn ₦1,000 per seeker • ₦5,000 per employer</p>
        </div>
        <nav className="p-4 space-y-2">
          {navItems.map(item => (
            <button 
              key={item.id} 
              onClick={() => { setTab(item.id); setSidebarOpen(false); }}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition ${tab === item.id ? "bg-purple-600 text-white" : "hover:bg-gray-100"}`}
            >
              {item.icon}
              <span className="font-medium">{item.name}</span>
            </button>
          ))}
          <button 
            onClick={() => {
              localStorage.clear();
              window.location.href = "/marketer/login";
            }} 
            className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left text-red-600 hover:bg-red-50 mt-8"
          >
            <LogOut className="w-5 h-5" />
            Logout
          </button>
        </nav>
      </div>

      {/* Main Content */}
      <div className="lg:ml-64 p-6 pt-20 lg:pt-8">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {tab === "dashboard" && <DashboardHome stats={data.stats} />}
          {tab === "create-seeker" && <CreateSeekerForm onSuccess={fetchData} />}
          {tab === "create-employer" && <CreateEmployerForm onSuccess={fetchData} />}
          {tab === "my-seekers" && <MySeekersList seekers={data.seekers} onCreateCV={setSelectedSeeker} />}
          {tab === "my-employers" && <MyEmployersList employers={data.employers} />}
        </div>
      </div>

      {/* CV Creator Modal */}
      {selectedSeeker && <CVCreatorModal seeker={selectedSeeker} onClose={() => setSelectedSeeker(null)} onSuccess={fetchData} />}
    </div>
  );
}

/* ALL SECTIONS – 100% COMPLETE */
function DashboardHome({ stats }) {
  // Fallback if no stats
  const dailyStats = stats.dailyStats || [];
  const weeklyStats = stats.weeklyStats || [];
  const monthlyStats = stats.monthlyStats || [];

  return (
    <div className="space-y-10">
      <h2 className="text-2xl md:text-3xl font-bold text-purple-700">Your Earnings Dashboard</h2>

      {/* Earnings Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-2xl shadow-lg">
          <Users className="w-12 h-12 text-blue-600 mb-3" />
          <p className="text-4xl font-bold text-blue-700">{stats.totalSeekers || 0}</p>
          <p className="text-base">Seekers Created</p>
          <p className="text-xl font-bold text-green-600 mt-1">₦{(stats.totalSeekers || 0) * 1000}</p>
        </div>
        <div className="bg-gradient-to-br from-green-50 to-green-100 p-6 rounded-2xl shadow-lg">
          <Building2 className="w-12 h-12 text-green-600 mb-3" />
          <p className="text-4xl font-bold text-green-700">{stats.totalEmployers || 0}</p>
          <p className="text-base">Employers Created</p>
          <p className="text-xl font-bold text-green-600 mt-1">₦{(stats.totalEmployers || 0) * 5000}</p>
        </div>
        <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-6 rounded-2xl shadow-lg">
          <DollarSign className="w-12 h-12 text-purple-600 mb-3" />
          <p className="text-4xl font-bold text-purple-700">₦{stats.totalEarnings || 0}</p>
          <p className="text-base">Total Earnings</p>
        </div>
        <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-6 rounded-2xl shadow-lg">
          <TrendingUp className="w-12 h-12 text-orange-600 mb-3" />
          <p className="text-4xl font-bold text-orange-700">₦{stats.payout30 || 0}</p>
          <p className="text-base">30% Ready for Payout</p>
        </div>
      </div>

      {/* Daily / Weekly / Monthly Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Daily */}
        <div className="bg-white p-6 rounded-2xl shadow-lg">
          <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
            <Calendar className="w-5 h-5" /> Daily Creation
          </h3>
          <div className="space-y-3">
            {dailyStats.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No data yet</p>
            ) : (
              dailyStats.slice(0, 7).reverse().map((d, i) => (
                <div key={i} className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">{d.date}</span>
                  <div className="flex gap-4">
                    <span className="text-blue-600 font-medium">{d.seekers} jobSeekers</span>
                    <span className="text-green-600 font-medium">{d.employers} JobEmployers</span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Weekly */}
        <div className="bg-white p-6 rounded-2xl shadow-lg">
          <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
            <Activity className="w-5 h-5" /> Weekly Creation
          </h3>
          <div className="space-y-3">
            {weeklyStats.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No data yet</p>
            ) : (
              weeklyStats.slice(0, 6).reverse().map((w, i) => (
                <div key={i} className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">{w.date}</span>
                  <div className="flex gap-4">
                    <span className="text-blue-600 font-medium">{w.seekers} jobSeekers</span>
                    <span className="text-green-600 font-medium">{w.employers} jobEmployers</span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Monthly */}
        <div className="bg-white p-6 rounded-2xl shadow-lg">
          <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5" /> Monthly Creation
          </h3>
          <div className="space-y-3">
            {monthlyStats.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No data yet</p>
            ) : (
              monthlyStats.slice(0, 6).reverse().map((m, i) => (
                <div key={i} className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">{m.date}</span>
                  <div className="flex gap-4">
                    <span className="text-blue-600 font-medium">{m.seekers} jobSeekers</span>
                    <span className="text-green-600 font-medium">{m.employers} jobEmployers</span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function CreateSeekerForm({ onSuccess }) {
  const [form, setForm] = useState({
    firstName: "", lastName: "", email: "", phoneNumber: "", gender: "male",
    address: "", state: "", localGovernment: "", country: "Nigeria"
  });
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    setSubmitting(true);
    const res = await fetch(`${process.env.REACT_APP_API_URL}marketer/create-seeker`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token")}` },
      body: JSON.stringify({ ...form, password: "12345678" })
    });
    const d = await res.json();
    alert(d.success ? "Seeker created! Password: 12345678" : d.message);
    d.success && onSuccess();
    setSubmitting(false);
  };

  return (
    <div>
      <h2 className="text-2xl font-bold mb-6">Create New Job Seeker</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl">
        <input placeholder="First Name *" className="p-3 border rounded-lg" onChange={e => setForm({...form, firstName: e.target.value})} />
        <input placeholder="Last Name *" className="p-3 border rounded-lg" onChange={e => setForm({...form, lastName: e.target.value})} />
        <input placeholder="Email *" type="email" className="p-3 border rounded-lg" onChange={e => setForm({...form, email: e.target.value})} />
        <input placeholder="Phone Number *" className="p-3 border rounded-lg" onChange={e => setForm({...form, phoneNumber: e.target.value})} />
        <input placeholder="Address *" className="p-3 border rounded-lg" onChange={e => setForm({...form, address: e.target.value})} />
        <input placeholder="State *" className="p-3 border rounded-lg" onChange={e => setForm({...form, state: e.target.value})} />
        <input placeholder="Local Government *" className="p-3 border rounded-lg" onChange={e => setForm({...form, localGovernment: e.target.value})} />
        <select className="p-3 border rounded-lg" onChange={e => setForm({...form, gender: e.target.value})}>
          <option value="male">Male</option>
          <option value="female">Female</option>
        </select>
      </div>
      <button onClick={submit} disabled={submitting} className="mt-6 bg-green-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-green-700 disabled:opacity-70 flex items-center gap-2">
        {submitting ? <><Loader2 className="animate-spin" /> Creating...</> : "Create Seeker"}
      </button>
    </div>
  );
}

function CreateEmployerForm({ onSuccess }) {
  const [form, setForm] = useState({
    companyName: "", email: "", phoneNumber: "", address: "", state: "", localGovernment: "", size: "", employerType: ""
  });
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    setSubmitting(true);
    const res = await fetch(`${process.env.REACT_APP_API_URL}marketer/create-employer`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token")}` },
      body: JSON.stringify({ ...form, password: "12345678" })
    });
    const d = await res.json();
    alert(d.success ? "Employer created!" : d.message);
    d.success && onSuccess();
    setSubmitting(false);
  };

  return (
    <div>
      <h2 className="text-2xl font-bold mb-6">Create New Employer</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl">
        <input placeholder="Company Name *" className="p-3 border rounded-lg" onChange={e => setForm({...form, companyName: e.target.value})} />
        <input placeholder="Email *" type="email" className="p-3 border rounded-lg" onChange={e => setForm({...form, email: e.target.value})} />
        <input placeholder="Phone Number *" className="p-3 border rounded-lg" onChange={e => setForm({...form, phoneNumber: e.target.value})} />
        <input placeholder="Address *" className="p-3 border rounded-lg" onChange={e => setForm({...form, address: e.target.value})} />
        <input placeholder="State *" className="p-3 border rounded-lg" onChange={e => setForm({...form, state: e.target.value})} />
        <input placeholder="Local Government *" className="p-3 border rounded-lg" onChange={e => setForm({...form, localGovernment: e.target.value})} />
        <input placeholder="Company Size" className="p-3 border rounded-lg" onChange={e => setForm({...form, size: e.target.value})} />
        <input placeholder="Employer Type" className="p-3 border rounded-lg" onChange={e => setForm({...form, employerType: e.target.value})} />
      </div>
      <button onClick={submit} disabled={submitting} className="mt-6 bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-70 flex items-center gap-2">
        {submitting ? <><Loader2 className="animate-spin" /> Creating...</> : "Create Employer"}
      </button>
    </div>
  );
}

function MySeekersList({ seekers, onCreateCV }) {
  return (
    <div>
      <h2 className="text-2xl font-bold mb-6">My Job Seekers ({seekers.length})</h2>
      <div className="space-y-4">
        {seekers.map(s => (
          <div key={s._id} className="bg-white p-6 rounded-lg shadow border flex justify-between items-center">
            <div>
              <p className="font-bold text-lg">{s.firstName} {s.lastName}</p>
              <p className="text-gray-600 flex items-center gap-2"><Mail className="w-4 h-4" /> {s.email}</p>
              <p className="text-gray-600 flex items-center gap-2"><Phone className="w-4 h-4" /> {s.phoneNumber}</p>
              <p className="text-sm text-gray-500 flex items-center gap-2"><Calendar className="w-4 h-4" /> {new Date(s.createdAt).toLocaleDateString()}</p>
            </div>
            <button onClick={() => onCreateCV(s)} className="bg-purple-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-purple-700 flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Create CV
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function MyEmployersList({ employers }) {
  return (
    <div>
      <h2 className="text-2xl font-bold mb-6">My Employers ({employers.length})</h2>
      <div className="space-y-4">
        {employers.map(e => (
          <div key={e._id} className="bg-white p-6 rounded-lg shadow border">
            <p className="font-bold text-lg">{e.companyName}</p>
            <p className="text-gray-600 flex items-center gap-2"><Mail className="w-4 h-4" /> {e.email}</p>
            <p className="text-gray-600 flex items-center gap-2"><Phone className="w-4 h-4" /> {e.phoneNumber}</p>
            <p className="text-sm text-gray-500 flex items-center gap-2"><Calendar className="w-4 h-4" /> {new Date(e.createdAt).toLocaleDateString()}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function CVCreatorModal({ seeker, onClose, onSuccess }) {
  const [cvData, setCvData] = useState({
    experience: "", education: "", skills: "", reference: "", jobChoice1: "", jobChoice2: "", jobChoice3: ""
  });
  const [template, setTemplate] = useState(1);
  const [generating, setGenerating] = useState(false);
  const [downloading, setDownloading] = useState(false);

  // LIVE PREVIEW FUNCTION — FULLY DEFINED & BEAUTIFUL
  const renderPreview = () => {
    const name = `${seeker.firstName} ${seeker.lastName}`.toUpperCase();
    const contact = `${seeker.email} • ${seeker.phoneNumber || "No phone"}`;

    // TEMPLATE 1 - MODERN
    if (template === 1) {
      return (
        <div className="bg-gradient-to-br from-purple-600 to-pink-600 text-white p-8 rounded-xl shadow-2xl">
          <h1 className="text-4xl font-bold mb-2">{name}</h1>
          <p className="text-lg opacity-90 mb-8">{contact}</p>
          
          <div className="space-y-6">
            <div className="bg-white/20 backdrop-blur p-6 rounded-xl">
              <h2 className="text-2xl font-bold mb-3">Experience</h2>
              <p className="text-lg leading-relaxed">{cvData.experience || "Your experience will appear here..."}</p>
            </div>
            <div className="bg-white/20 backdrop-blur p-6 rounded-xl">
              <h2 className="text-2xl font-bold mb-3">Education</h2>
              <p className="text-lg leading-relaxed">{cvData.education || "Your qualifications will appear here..."}</p>
            </div>
            <div className="bg-white/20 backdrop-blur p-6 rounded-xl">
              <h2 className="text-2xl font-bold mb-3">Skills</h2>
              <p className="text-lg leading-relaxed">{cvData.skills || "Your skills will appear here..."}</p>
            </div>
            <div className="bg-white/20 backdrop-blur p-6 rounded-xl">
              <h2 className="text-2xl font-bold mb-3">Job Preferences</h2>
              <p className="text-lg">
                1st: {cvData.jobChoice1 || "N/A"} | 2nd: {cvData.jobChoice2 || "N/A"} | 3rd: {cvData.jobChoice3 || "N/A"}
              </p>
            </div>
          </div>
        </div>
      );
    }

    // TEMPLATE 2 - CLASSIC
    if (template === 2) {
      return (
        <div className="bg-white border-4 border-gray-800 p-10 rounded-xl">
          <header className="text-center border-b-4 border-double border-gray-800 pb-8 mb-10">
            <h1 className="text-5xl font-serif mb-4">{name}</h1>
            <p className="text-xl">{contact}</p>
          </header>
          <section className="space-y-8">
            <div>
              <h2 className="text-2xl font-bold border-b-2 border-gray-800 pb-2 mb-4">Professional Experience</h2>
              <p className="text-lg leading-relaxed">{cvData.experience || "Your experience will appear here..."}</p>
            </div>
            <div>
              <h2 className="text-2xl font-bold border-b-2 border-gray-800 pb-2 mb-4">Education</h2>
              <p className="text-lg leading-relaxed">{cvData.education || "Your qualifications will appear here..."}</p>
            </div>
            <div>
              <h2 className="text-2xl font-bold border-b-2 border-gray-800 pb-2 mb-4">Skills</h2>
              <p className="text-lg leading-relaxed">{cvData.skills || "Your skills will appear here..."}</p>
            </div>
          </section>
        </div>
      );
    }

    // TEMPLATE 3 - MINIMAL
    return (
      <div className="bg-white p-10 rounded-xl shadow-inner">
        <h1 className="text-5xl font-light tracking-widest border-b-4 border-gray-300 pb-6 mb-8">
          {name}
        </h1>
        <p className="text-xl text-gray-600 mb-12">{contact}</p>
        
        <div className="space-y-10">
          <div>
            <h3 className="text-2xl font-bold mb-4">EXPERIENCE</h3>
            <p className="text-lg leading-relaxed text-gray-700">{cvData.experience || "Your experience will appear here..."}</p>
          </div>
          <div>
            <h3 className="text-2xl font-bold mb-4">EDUCATION</h3>
            <p className="text-lg leading-relaxed text-gray-700">{cvData.education || "Your qualifications will appear here..."}</p>
          </div>
          <div>
            <h3 className="text-2xl font-bold mb-4">SKILLS</h3>
            <p className="text-lg leading-relaxed text-gray-700">{cvData.skills || "Your skills will appear here..."}</p>
          </div>
          <div>
            <h3 className="text-2xl font-bold mb-4">JOB PREFERENCES</h3>
            <p className="text-lg leading-relaxed text-gray-700">
              1st: {cvData.jobChoice1 || "N/A"} | 2nd: {cvData.jobChoice2 || "N/A"} | 3rd: {cvData.jobChoice3 || "N/A"}
            </p>
          </div>
        </div>
      </div>
    );
  };

  // Generate & Send Email
  const generateAndSend = async () => {
    setGenerating(true);
    const res = await fetch(`${process.env.REACT_APP_API_URL}marketer/generate-cv`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token")}` },
      body: JSON.stringify({ seekerId: seeker._id, cvData, template })
    });
    const d = await res.json();
    alert(d.success ? `CV sent to ${seeker.email}!` : d.message);
    if (d.success) onSuccess();
     setGenerating(false);
    onClose();
  };

  // Download PDF
  const downloadPDF = async () => {
    setDownloading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}marketer/generate-cv`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token")}` },
        body: JSON.stringify({ seekerId: seeker._id, cvData, template, download: true })
      });

      if (!res.ok) throw new Error("Failed");

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${seeker.firstName}_${seeker.lastName}_CV.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);

      alert("CV downloaded successfully!");
    } catch (err) {
      alert("Download failed");
    }
    setDownloading(false);
  };

  // WhatsApp Share
  const shareWhatsApp = () => {
    const text = `Hello ${seeker.firstName}! Your CV is ready! Check your email or download from your marketer.`;
    const url = `https://wa.me/${seeker.phoneNumber}?text=${encodeURIComponent(text)}`;
    window.open(url, "_blank");
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-3xl shadow-3xl w-full max-w-6xl my-8 flex flex-col max-h-screen">
        {/* Header – STICKY & ALWAYS VISIBLE */}
        <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white p-6 rounded-t-3xl sticky top-0 z-10 flex justify-between items-center">
          <h2 className="text-xl md:text-2xl font-bold">
            Create CV for {seeker.firstName} {seeker.lastName}
          </h2>
          <button onClick={onClose} className="text-white hover:bg-white/20 p-2 rounded-full transition">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-6 md:p-8">
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Form */}
            <div className="space-y-5">
              <textarea placeholder="Work Experience" rows="5" className="w-full p-4 border-2 rounded-xl focus:border-purple-500 text-base" onChange={e => setCvData({...cvData, experience: e.target.value})} />
              <textarea placeholder="Education" rows="5" className="w-full p-4 border-2 rounded-xl focus:border-purple-500 text-base" onChange={e => setCvData({...cvData, education: e.target.value})} />
              <textarea placeholder="Skills" rows="5" className="w-full p-4 border-2 rounded-xl focus:border-purple-500 text-base" onChange={e => setCvData({...cvData, skills: e.target.value})} />
              <div className="grid grid-cols-1 gap-4">
                <input placeholder="1st Job Choice" className="p-4 border-2 rounded-xl text-base" onChange={e => setCvData({...cvData, jobChoice1: e.target.value})} />
                <input placeholder="2nd Job Choice" className="p-4 border-2 rounded-xl text-base" onChange={e => setCvData({...cvData, jobChoice2: e.target.value})} />
                <input placeholder="3rd Job Choice" className="p-4 border-2 rounded-xl text-base" onChange={e => setCvData({...cvData, jobChoice3: e.target.value})} />
              </div>
            </div>

            {/* Preview */}
            <div className="space-y-6">
              <h3 className="text-xl font-bold">Choose Template</h3>
              <div className="grid grid-cols-3 gap-4">
                {[1, 2, 3].map(t => (
                  <button key={t} onClick={() => setTemplate(t)} className={`border-4 rounded-xl overflow-hidden transition-all ${template === t ? "border-purple-600 shadow-2xl scale-105" : "border-gray-300"}`}>
                    <div className="bg-gray-200 h-32 rounded"></div>
                    <p className="text-center font-bold py-3">Template {t}</p>
                  </button>
                ))}
              </div>

              <div className="bg-gray-50 p-6 rounded-2xl border-2 border-purple-200">
                <h3 className="text-xl font-bold text-center mb-4">Live Preview</h3>
                <div className="bg-white rounded-xl shadow-inner p-6">
                  {renderPreview()}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Fixed Buttons at Bottom – NORMAL SIZE */}
        <div className="border-t bg-gray-50 p-6 flex flex-col sm:flex-row gap-4 justify-center">
          <button onClick={downloadPDF} disabled={downloading} className="bg-blue-600 text-white px-8 py-3 rounded-lg font-medium hover:bg-blue-700 disabled:opacity-70 flex items-center justify-center gap-2">
            {downloading ? <><Loader2 className="animate-spin w-5 h-5" /> Downloading...</> : "Download PDF"}
          </button>
          <button onClick={generateAndSend} disabled={generating} className="bg-green-600 text-white px-8 py-3 rounded-lg font-medium hover:bg-green-700 disabled:opacity-70 flex items-center justify-center gap-2">
            {generating ? <><Loader2 className="animate-spin w-5 h-5" /> Sending...</> : "Send to Email"}
          </button>
          <button onClick={shareWhatsApp} className="bg-green-500 text-white px-8 py-3 rounded-lg font-medium hover:bg-green-600 flex items-center justify-center gap-2">
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.297-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.626.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.485"/></svg>
            WhatsApp
          </button>
          <button onClick={onClose} className="bg-red-600 text-white px-8 py-3 rounded-lg font-medium hover:bg-red-700">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}